import chevronLeftIcon from "../../icons/chevron-left.svg?raw";
import chevronRightIcon from "../../icons/chevron-right.svg?raw";
import { closest, createElement as h, replaceChildren } from "../utils/dom";
import ScrollObserver from "../interfaces/ScrollObserver";

export default class Booklet extends HTMLElement {
  /**
   * Define the element in the document's custom element registry.
   *
   * @param {string} [tag] The tag to use in the element definition
   */
  static define(tag = "ifrs-booklet") {
    if (!customElements.get(tag)) {
      customElements.define(tag, this);
    }
  }

  constructor() {
    super();
    this.cleanup = new Set();
    this.scrollObserver = new ScrollObserver((entry) => {
      if (entry.signX) {
        const direction = entry.signX > 0 ? "ltr" : "rtl";
        if (direction !== this.getAttribute("scroll-direction")) {
          this.setAttribute("scroll-direction", direction);
          Array.from(this.querySelectorAll(".booklet-spread")).forEach(
            (el, i, arr) => {
              el.style.zIndex = entry.signX > 0 ? i : arr.length - i;
            },
          );
        }
      }
    });
  }

  /**
   * A collection for side effect cleanup functions. Each function in the set
   * is called and removed when the component is disconnected.
   *
   * @type {Set<() => void>}
   */
  cleanup;

  /**
   * Get the index of the current spread.
   */
  get current() {
    const value = parseInt(this.getAttribute("current"), 10);
    return Number.isInteger(value) ? value : 0;
  }

  /**
   * Set the index of the current spread.
   */
  set current(value) {
    const index = parseInt(value, 10);
    if (
      Number.isInteger(index) &&
      index >= 0 &&
      index < this.querySelectorAll(".booklet-spread").length
    ) {
      this.setAttribute("current", index.toString());
    }
  }

  /**
   * @type {ScrollObserver}
   */
  scrollObserver;

  /**
   * Reacts to changes of observed attributes. The function makes DOM updates
   * to reflect the current state of the component.
   *
   * @param {string} name The changed attribute name
   * @param {*} prev The previous value of the attribute
   * @param {*} next The new value of the attribute
   */
  attributeChangedCallback(name, prev, next) {
    if (name === "current") {
      const index = parseInt(next, 10);
      if (!Number.isInteger(index)) {
        this.current = 0;
        return;
      }
      const spreads = this.querySelector(".booklet-spreads");
      if (spreads) {
        const width = Math.round(
          spreads.scrollWidth / spreads.childElementCount,
        );
        const scrollTo = index * width;
        spreads.scrollTo({ behavior: "smooth", left: scrollTo });
        this.toggleAttribute(
          "scrollend",
          scrollTo + width >= spreads.scrollWidth,
        );
        this.toggleAttribute("scrollstart", scrollTo === 0);
      }
    }
  }

  /**
   * Set up the component once it has been added to the DOM.
   */
  connectedCallback() {
    // Exit early if the component is not connected.
    if (!this.isConnected) {
      return;
    }

    // Set up component markup
    this.setup();

    const spreads = this.querySelector(".booklet-spreads");
    if (spreads) {
      this.scrollObserver.observe(spreads);
      this.cleanup.add(() => this.scrollObserver.unobserve(spreads));
    }

    const navigation = this.querySelector(".booklet-navigation");
    if (navigation) {
      /**
       * @param {PointerEvent} event
       */
      const handleNavigationClick = (event) => {
        const button = closest(
          event.target,
          ".booklet-navigation button[data-rel]",
        );
        if (button) {
          this.current =
            this.current + (button.dataset.rel === "next" ? 1 : -1);
        }
      };
      this.addEventListener("click", handleNavigationClick);
      this.cleanup.add(() =>
        this.removeEventListener("click", handleNavigationClick),
      );
    }
  }

  /**
   * Clean up side effects when the component is disconnected.
   */
  disconnectedCallback() {
    // Call and delete all cleanup functions
    if (this.cleanup) {
      this.cleanup.forEach((fn) => fn());
      this.cleanup.clear();
    }
  }

  /**
   * Set up the component markup.
   *
   * Note that this method may be called repeatedly and should be idempotent.
   */
  setup() {
    if (!this.querySelector(".booklet-spreads")) {
      const spreads = [];
      Array.from(this.children).forEach((el, i) => {
        let spread = spreads[Math.floor(i / 2)];
        if (!spread) {
          spread = h("div", { className: "booklet-spread" });
          spreads.push(spread);
        }
        spread.append(el);
      });
      replaceChildren(
        this,
        h("div", { className: "booklet-spreads", tabIndex: -1 }, spreads),
      );
    }

    if (!this.querySelector(".booklet-navigation")) {
      const navigation = h("div", { className: "booklet-navigation" }, [
        h(
          "button",
          {
            "aria-label": "Previous",
            dataset: { rel: "prev" },
            type: "button",
          },
          [
            h(chevronLeftIcon, {
              "aria-hidden": "true",
              classList: "icon",
              xmlns: null,
            }),
          ],
        ),
        h(
          "button",
          { "aria-label": "Next", dataset: { rel: "next" }, type: "button" },
          [
            h(chevronRightIcon, {
              "aria-hidden": "true",
              classList: "icon",
              xmlns: null,
            }),
          ],
        ),
      ]);
      this.append(navigation);
      this.toggleAttribute("scrollstart", true);
      this.toggleAttribute(
        "scrollend",
        this.querySelectorAll(".booklet-spread").length < 2,
      );
    }
  }
}

Object.assign(Booklet, { observedAttributes: ["current"] });
